import { useState } from "react";
import toast from "react-hot-toast";
import api from "../utils/common/axios";

const MAX_FILE_SIZE_MB = 50;
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/webp",
];
const ALLOWED_ATTACHMENT_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

/**
 * Direct async upload helper for Minio
 * @param {File|Blob} file 
 * @param {string} [fileDestination="garments"] 
 * @param {object} [options={}] 
 * @returns {Promise<string>} File URL
 */
export const uploadFileToMinio = async (
  file,
  fileDestination = "garments",
  options = {}
) => {
  if (!file) return null;
  const formData = new FormData();
  formData.append("file", file);
  if (fileDestination) {
    formData.append("fileDestination", fileDestination);
  }

  const response = await api.post("km2/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    ...options,
  });

  const fileUrl =
    response?.data?.url ||
    response?.data?.data?.url ||
    response?.data?.fileUrl ||
    response?.data?.path ||
    response?.data ||
    "";

  return fileUrl;
};

export const useS3Upload = () => {
  const [image, setImage] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (
    key,
    file,
    fileDestination = "garments",
    toastIs,
    allowedTypes = ALLOWED_IMAGE_TYPES,
    maxSizeMB = MAX_FILE_SIZE_MB
  ) => {
    if (!file) return;

    /* -------------------- FILE TYPE VALIDATION -------------------- */
    if (Array.isArray(allowedTypes) && allowedTypes.length && !allowedTypes.includes(file.type)) {
      toast.error("This file type is not supported");
      return;
    }

    /* -------------------- SIZE VALIDATION -------------------- */
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      toast.error(`File size should not exceed ${maxSizeMB} MB`);
      return;
    }

    /* -------------------- UPLOAD -------------------- */
    const formData = new FormData();
    formData.append("file", file);
    if (fileDestination) {
      formData.append("fileDestination", fileDestination);
    }

    setIsUploading(true);
    try {
      const response = await api.post("km2/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200 || response.status === 201) {
        if (toastIs !== false) {
          toast.success(
            response.data?.message || "File Uploaded Successfully"
          );
        }
        const fileUrl =
          response?.data?.url ||
          response?.data?.data?.url ||
          response?.data?.fileUrl ||
          response?.data?.path ||
          response?.data ||
          "";

        setImage((prev) => ({
          ...prev,
          [key]: fileUrl,
        }));

        return fileUrl;
      } else {
        toast.error("File Upload Failed");
      }
    } catch (exception) {
      console.error(exception);
      toast.error(exception?.message || "File Upload Failed");
    } finally {
      setIsUploading(false);
    }
  };

  return [image, handleUpload, isUploading];
};

export const useMinioUpload = useS3Upload;
export const TASK_ATTACHMENT_TYPES = ALLOWED_ATTACHMENT_TYPES;
export default useS3Upload;
